/**
 * ZenMarkup Privacy Analytics — REST API module.
 *
 * All fetch() calls to the plugin REST endpoints live here.
 * No DOM access — returns data only.
 */

import * as state from './state.js';

/**
 * Build the query string for the stats endpoint.
 *
 * @return {string}
 */
function buildStatsQuery() {
	const range = state.getRange();

	if (range === 'custom') {
		return 'stats?period=custom'
			+ '&from=' + encodeURIComponent(state.getStartDate())
			+ '&to=' + encodeURIComponent(state.getEndDate());
	}

	return 'stats?period=' + encodeURIComponent(range);
}

/**
 * Build a cache key from the current state.
 *
 * @return {string}
 */
function cacheKey() {
	return state.getRange() + '_' + state.getStartDate() + '_' + state.getEndDate();
}

/**
 * Build the query string for the cities endpoint.
 *
 * @param {string} country  Country name to fetch cities for.
 * @return {string}
 */
function buildCitiesQuery(country) {
	const range = state.getRange();
	let qs = 'stats?period=' + encodeURIComponent(range)
		+ '&city_country=' + encodeURIComponent(country);

	if (range === 'custom') {
		qs = 'stats?period=custom'
			+ '&from=' + encodeURIComponent(state.getStartDate())
			+ '&to=' + encodeURIComponent(state.getEndDate())
			+ '&city_country=' + encodeURIComponent(country);
	}

	return qs;
}

/**
 * Check if demo mode is active (zmpaDemoData global loaded as separate script).
 *
 * @return {boolean}
 */
function isDemoMode() {
	return typeof window.zmpaDemoData !== 'undefined';
}

/**
 * Fetch aggregated stats from the REST endpoint.
 *
 * @return {Promise<Object|null>}
 */
export async function fetchStats() {
	if (isDemoMode()) {
		const demoResult = window.zmpaDemoData.get();
		state.setCache(cacheKey(), demoResult);
		return demoResult;
	}

	const key = cacheKey();
	const cached = state.getCache(key);

	if (cached) {
		return cached;
	}

	try {
		const response = await fetch(window.zmpaDashData.restUrl + buildStatsQuery(), {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'X-WP-Nonce': window.zmpaDashData.nonce
			}
		});

		if (!response.ok) {
			throw new Error('HTTP ' + response.status);
		}

		const json = await response.json();

		if (!json.success) {
			throw new Error('API returned success=false');
		}

		state.setCache(key, json.data);
		return json.data;
	} catch (err) {
		console.error('[ZenMarkup Privacy Analytics] Failed to fetch stats:', err);
		return null;
	}
}

/**
 * Fetch live online visitor count.
 *
 * @return {Promise<number|null>}
 */
export async function fetchOnline() {
	if (isDemoMode()) {
		const demo = window.zmpaDemoData.get();
		return demo.online || 0;
	}

	try {
		const response = await fetch(window.zmpaDashData.restUrl + 'online', {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'X-WP-Nonce': window.zmpaDashData.nonce
			}
		});

		if (!response.ok) {
			throw new Error('HTTP ' + response.status);
		}

		const json = await response.json();
		return json.count || 0;
	} catch (err) {
		console.error('[ZenMarkup Privacy Analytics] Failed to fetch online count:', err);
		return null;
	}
}

/**
 * Fetch cities for a specific country.
 *
 * @param {string} country  Country name.
 * @return {Promise<Array|null>}
 */
export async function fetchCities(country) {
	if (isDemoMode()) {
		const demo = window.zmpaDemoData.get();
		const byCountry = demo.cities_by_country || {};
		return byCountry[country] || [];
	}

	try {
		const response = await fetch(window.zmpaDashData.restUrl + buildCitiesQuery(country), {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'X-WP-Nonce': window.zmpaDashData.nonce
			}
		});

		if (!response.ok) {
			throw new Error('HTTP ' + response.status);
		}

		const json = await response.json();

		if (!json.success || !json.data) {
			throw new Error('API returned success=false');
		}

		return json.data.cities || [];
	} catch (err) {
		console.error('[ZenMarkup Privacy Analytics] Failed to fetch cities:', err);
		return null;
	}
}
